import React, { useState } from "react";
import { 
  FaShieldAlt, 
  FaChevronRight, 
  FaRegClock, 
  FaLock, 
  FaUserShield, 
  FaHistory, 
  FaEnvelope,
  FaExclamationCircle,
  FaBalanceScale,
  FaBan
} from "react-icons/fa";
import { MdOutlineSecurity, MdOutlineFactCheck, MdGavel } from "react-icons/md";

const ZaffloTerms = ({ lastUpdated = "February 19, 2026" }) => {
  const menuItems = [
    { id: "purpose", label: "Roles & Purpose", icon: <MdOutlineFactCheck /> },
    { id: "compliance", label: "Business Compliance", icon: <FaBalanceScale /> },
    { id: "data", label: "Data Governance", icon: <FaUserShield /> },
    { id: "liability", label: "Liability & Indemnity", icon: <MdGavel /> },
    { id: "retention", label: "Data Lifecycle", icon: <FaHistory /> },
    { id: "refunds", label: "Subscription Terms", icon: <FaShieldAlt /> },
    { id: "ip", label: "Intellectual Property", icon: <FaShieldAlt /> },
    { id: "governing-law", label: "Jurisdiction", icon: <FaBalanceScale /> },
  ];

  return (
    <div className="min-h-screen bg-[#fcfcfd] text-slate-800 font-sans selection:bg-[#6cbb23]/20">
      
      {/* --- STICKY HEADER --- */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-[#6cbb23] p-2.5 rounded-xl shadow-lg shadow-[#6cbb23]/20">
              <FaShieldAlt className="text-white text-xl" />
            </div>
            <div>
              <span className="text-xl font-bold tracking-tight text-slate-900 block leading-none">
                Zafflo <span className="text-[#6cbb23]">Legal</span>
              </span>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                DPDPA 2023 Compliant Portal
              </span>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-2 text-sm font-medium text-slate-500 bg-slate-100/80 px-4 py-2 rounded-full">
            <FaRegClock className="text-[#6cbb23]" />
            Last Updated: {lastUpdated}
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12 flex flex-col lg:flex-row gap-16">
        
        {/* --- SIDEBAR --- */}
        <aside className="lg:w-64 flex-shrink-0">
          <div className="sticky top-32 space-y-1">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest ml-4 mb-4">
              Legal Framework
            </p>
            {menuItems.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-all duration-200 group"
              >
                <span className="text-lg group-hover:text-[#6cbb23] transition-colors">
                  {item.icon}
                </span>
                <span className="text-sm font-semibold">{item.label}</span>
              </a>
            ))}
            
            <div className="mt-10 p-5 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl text-white shadow-xl">
              <p className="text-xs font-medium opacity-70 mb-1 font-mono">Grievance Officer</p>
              <p className="text-sm font-bold mb-4 leading-snug">Data Protection Inquiry?</p>
              <a href="mailto:wecare@zafflo.com" className="flex items-center justify-center gap-2 bg-[#6cbb23] hover:bg-[#5aa81e] transition-all py-2.5 rounded-lg text-xs font-bold">
                <FaEnvelope /> Contact Officer
              </a>
            </div>
          </div>
        </aside>

        {/* --- MAIN CONTENT --- */}
        <main className="flex-1 max-w-3xl">
          
          <header className="mb-16">
            <h1 className="text-4xl md:text-5xl font-black text-slate-900 tracking-tight mb-6">
              Terms of Service
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              This Agreement governs the relationship between <span className="text-slate-900 font-bold">Zafflo (The Platform)</span> and the 
              <span className="text-slate-900 font-bold"> Business (The Restaurant/Hotel)</span> under the Indian Digital Personal Data Protection Act (DPDPA) 2023.
            </p>
          </header>

          <div className="space-y-20">
            
            {/* 1. Legal Roles */}
            <section id="purpose" className="scroll-mt-32">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-8 w-1 bg-[#6cbb23] rounded-full" />
                <h2 className="text-2xl font-bold text-slate-900">1. Relationship & Roles</h2>
              </div>
              <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4 text-slate-600">
                <p>
                  <strong>Zafflo (Data Processor):</strong> Acts solely on the instructions of the Business to facilitate digital ordering and menu management.
                </p>
                <p>
                  <strong>The Business (Data Fiduciary):</strong> The Restaurant or Hotel using Zafflo is the primary custodian of customer data. They are responsible for determining why and how personal data is processed.
                </p>
              </div>
            </section>

            {/* 2. Business Compliance */}
            <section id="compliance" className="scroll-mt-32">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-8 w-1 bg-[#6cbb23] rounded-full" />
                <h2 className="text-2xl font-bold text-slate-900">2. Business Responsibilities</h2>
              </div>
              <p className="text-slate-600 mb-6">As a Data Fiduciary, the Business warrants and represents that it shall:</p>
              <ul className="space-y-3">
                {[
                  "Obtain explicit, informed consent from customers before marketing.",
                  "Ensure all data usage complies with DPDPA 2023 and local laws.",
                  "Honor customer requests for data deletion or correction.",
                  "Notify Zafflo immediately of any internal security breaches."
                ].map((text, i) => (
                  <li key={i} className="flex gap-3 text-sm text-slate-600">
                    <FaChevronRight className="text-[#6cbb23] mt-1 flex-shrink-0" />
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* 3. Downstream Liability Shield */}
            <section id="data" className="scroll-mt-32">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-8 w-1 bg-[#6cbb23] rounded-full" />
                <h2 className="text-2xl font-bold text-slate-900">3. Downstream Data Usage</h2>
              </div>
              <div className="p-6 bg-blue-50/50 border border-blue-100 rounded-3xl">
                <div className="flex items-start gap-4">
                  <MdOutlineSecurity className="text-3xl text-blue-600 mt-1" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-2">Scope of Control</h4>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Zafflo’s responsibility is limited to the security of the Platform. Zafflo is <strong>not liable</strong> for data once it is accessed, printed, exported, or downloaded by the Business. The Business is solely responsible for the security of its own devices, staff handling, and local storage.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* 4. Liability & Indemnity - THE SHIELD */}
            <section id="liability" className="scroll-mt-32">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-8 w-1 bg-slate-900 rounded-full" />
                <h2 className="text-2xl font-bold text-slate-900">4. Indemnity & Limitation</h2>
              </div>
              <div className="space-y-4">
                <div className="bg-slate-900 rounded-2xl p-6 text-white shadow-xl">
                  <h4 className="font-bold mb-3 flex items-center gap-2">
                    <MdGavel className="text-[#6cbb23]" /> Indemnification
                  </h4>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    The Business agrees to indemnify, defend, and hold Zafflo harmless from any claims, damages, or legal penalties arising from the Business’s misuse of data, unauthorized marketing (spamming), or failure to comply with Indian Privacy Laws.
                  </p>
                </div>
                <div className="bg-white border border-slate-200 rounded-2xl p-6">
                  <h4 className="font-bold text-slate-900 mb-2">Financial Liability Cap</h4>
                  <p className="text-sm text-slate-600">
                    To the maximum extent permitted by law, Zafflo’s total aggregate liability for any claim arising from this Agreement is limited to the **total fees paid by the Business to Zafflo in the three (3) months** immediately preceding the event.
                  </p>
                </div>
              </div>
            </section>

            {/* 5. Termination */}
            <section id="termination" className="scroll-mt-32">
               <div className="flex items-center gap-3 mb-6 text-red-600">
                <FaBan />
                <h2 className="text-2xl font-bold">5. Zero-Tolerance for Misuse</h2>
              </div>
              <p className="text-slate-600 text-sm leading-relaxed">
                Zafflo reserves the right to suspend or terminate access to the Platform immediately, without notice or refund, if there is a verified report of data resale, unauthorized export of customer lists, or staff negligence leading to a breach by the Business.
              </p>
            </section>

            {/* 6. Refund Section */}
            <section id="refunds" className="scroll-mt-32">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-8 w-1 bg-red-500 rounded-full" />
                <h2 className="text-2xl font-bold text-slate-900">6. Subscription & Refunds</h2>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-5 bg-red-50 border border-red-100 rounded-2xl">
                   <p className="font-bold text-red-900 text-sm">Strictly Non-Refundable</p>
                   <p className="text-red-700/80 text-[11px] mt-1">
                     Fees are non-refundable regardless of business status or usage volume.
                   </p>
                </div>
                <div className="p-5 bg-blue-50 border border-blue-100 rounded-2xl">
                   <p className="font-bold text-blue-900 text-sm">Billing Errors</p>
                   <p className="text-blue-700/80 text-[11px] mt-1">
                     Only duplicate payments or technical verification errors are eligible for review.
                   </p>
                </div>
              </div>
            </section>

            {/* 7. Intellectual Property */}
            <section id="ip" className="scroll-mt-32">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-8 w-1 bg-[#6cbb23] rounded-full" />
                <h2 className="text-2xl font-bold text-slate-900">7. Intellectual Property</h2>
              </div>
              <p className="text-slate-600 text-sm leading-relaxed">
                All software, algorithms, UI designs, and branding associated with Zafflo are the exclusive property of Zafflo Solutions. The Business is granted a <strong>non-exclusive, non-transferable license</strong> to use the platform. Any attempt to reverse-engineer, copy, or "scrape" the platform's architecture will result in immediate legal action.
              </p>
            </section>

            {/* 8. Governing Law - CRITICAL FOR INDIA */}
            <section id="governing-law" className="scroll-mt-32">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-8 w-1 bg-slate-900 rounded-full" />
                <h2 className="text-2xl font-bold text-slate-900">8. Governing Law & Jurisdiction</h2>
              </div>
              <div className="bg-slate-100 p-6 rounded-2xl border border-slate-200">
                <p className="text-sm text-slate-700 leading-relaxed font-medium">
                  This Agreement shall be governed by and construed in accordance with the laws of India. Any disputes arising out of or in connection with these terms shall be subject to the exclusive jurisdiction of the courts located in <span className="text-[#6cbb23] underline decoration-dotted">Pune</span>, India.
                </p>
              </div>
            </section>

          </div>

          <footer className="mt-32 pt-12 border-t border-slate-200">
             <div className="flex flex-col md:flex-row justify-between items-center gap-6">
               <p className="text-sm text-slate-400 font-medium">
                 © {new Date().getFullYear()} Zafflo Solutions. Registered in India.
               </p>
               <div className="flex gap-6">
                 <a href="#" className="text-xs font-bold text-slate-400 hover:text-[#6cbb23] tracking-widest uppercase">Compliance Desk</a>
                 <a href="mailto:wecare@zafflo.com" className="text-xs font-bold text-[#6cbb23] tracking-widest uppercase underline">Grievance Portal</a>
               </div>
             </div>
          </footer>
        </main>
      </div>
    </div>
  );
};

export default ZaffloTerms;